<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  newVersion: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'dismiss'])

const updating = ref(false)

watch(
  () => props.modelValue,
  (open) => {
    if (open) updating.value = false
  }
)

function onConfirm() {
  updating.value = true
  emit('confirm')
}

function onDismiss() {
  emit('dismiss')
  emit('update:modelValue', false)
}
</script>

<template>
  <Transition name="fade">
    <div v-if="modelValue" class="update-modal-overlay" role="dialog" aria-modal="true">
      <div class="update-modal">
        <div class="update-modal-icon">
          <svg
            viewBox="0 0 24 24"
            width="48"
            height="48"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </div>

        <h2 class="update-modal-title">גרסה חדשה זמינה</h2>
        <p class="update-modal-subtitle">
          שחררנו עדכון למערכת. לחיצה על "עדכן עכשיו" תטען את הגרסה החדשה.
        </p>

        <div v-if="newVersion" class="update-modal-version">גרסה {{ newVersion }}</div>

        <div class="update-modal-actions">
          <button class="btn btn-secondary" :disabled="updating" @click="onDismiss">אחר כך</button>
          <button class="btn btn-primary" :disabled="updating" @click="onConfirm">
            <span v-if="updating" class="spinner" aria-hidden="true" />
            <span>{{ updating ? 'מעדכן...' : 'עדכן עכשיו' }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.update-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
  backdrop-filter: blur(4px);
}

.update-modal {
  background: var(--bg-primary);
  border-radius: 18px;
  padding: 32px 28px 24px;
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  direction: rtl;
}

.update-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--gradient-primary);
  color: #fff;
  margin-bottom: 16px;
  box-shadow: 0 6px 16px rgba(30, 41, 59, 0.25);
}

.update-modal-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.update-modal-subtitle {
  font-size: 0.95rem;
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 0 0 12px 0;
}

.update-modal-version {
  display: inline-block;
  font-size: 0.8rem;
  color: var(--color-primary);
  background: var(--bg-tertiary);
  padding: 4px 14px;
  border-radius: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  font-variant-numeric: tabular-nums;
}

.update-modal-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 24px;
  font-size: 0.95rem;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
  font-family: inherit;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  color: #fff;
  background: var(--gradient-primary);
  box-shadow: 0 2px 6px rgba(30, 41, 59, 0.2);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(30, 41, 59, 0.3);
}

.btn-secondary {
  color: var(--text-primary);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 480px) {
  .update-modal {
    padding: 24px 20px 20px;
  }
  .update-modal-title {
    font-size: 1.2rem;
  }
  .btn {
    flex: 1;
    min-width: 0;
  }
}
</style>
